import type { Except } from '@festina-life/core'
import cx from 'classnames'
import { useEffect, useEffectEvent, useRef, useState } from 'react'

import { RawSelect } from '../select/RawSelect'
import { RawTextInput, type RawTextInputProps } from '../textInput/RawTextInput'

type UnresolvedFormValue = {
  /**
   * If `optionsAddOnProps` was given, this will be one of those options.
   * Otherwise, this value is `undefined`.
   */
  option: string | undefined
  /**
   * Non-empty string with the current form value that will be submitted.
   */
  formValue: string
}

type ResolvedFormValue = {
  /** Non-empty string that the user-visible text input will be set to. */
  userInput: string
  /** Formatted display name for the resolved input. */
  display: string
}

type UnresolvedUserInput = {
  /**
   * If `optionsAddOnProps` was given, this will be one of those options.
   * Otherwise, this value is `undefined`.
   */
  option: string | undefined
  /** Non-empty string of user-inputted text to be resolved before it can be set as the form value. */
  userInput: string
}

type ResolvedUserInput = {
  /** Non-empty string to be stored in as the form value to be submitted. */
  formValue: string
  /** Formatted display name for the resolved input. */
  display: string
}

export type RawLazyTextInputProps = Except<
  RawTextInputProps,
  'onChange' | 'onError'
> & {
  name?: string
  touched: boolean
  onChange?: (value: ResolvedUserInput['formValue'] | undefined) => void
  onChangeDisplay?: (display: ResolvedUserInput['display'] | undefined) => void
  onIsLoading?: (isLoading: boolean) => void
  onError?: (error: Error | undefined) => void
  /** Should throw an `Error` if the resolution cannot be performed for any reason. */
  resolveFormValue: (
    unresolvedFormValue: UnresolvedFormValue,
  ) => Promise<ResolvedFormValue>
  /** Should throw an `Error` if the resolution cannot be performed for any reason. */
  resolveUserInput: (
    unresolvedUserInput: UnresolvedUserInput,
  ) => Promise<ResolvedUserInput>
  optionsAddOnProps?: {
    'aria-label': string
    /** Classname applied to the select's input element */
    className?: string
    options: readonly string[]
    renderFn: (option: string) => string
  }
}

export const RawLazyTextInput = ({
  name,
  value: formValue,
  disabled,
  touched,
  onChange,
  onChangeDisplay,
  onIsLoading,
  onError,
  resolveFormValue,
  resolveUserInput,
  optionsAddOnProps,
  isInError = false,
  ...restProps
}: RawLazyTextInputProps) => {
  const [userInput, setUserInput] = useState<string>('')
  const [option, setOption] = useState(
    optionsAddOnProps?.options[0] ?? undefined,
  )
  const rawInputRef = useRef<HTMLInputElement>(null)

  const [isResolvingFormValue, setIsResolvingFormValue] = useState(
    !!formValue, // Optimization: initialValue can be the same as-if the effect had already ran for the first time
  )
  const formValueBeingResolved = useRef<UnresolvedFormValue | null>(null)
  const userInputBeingResolved = useRef<UnresolvedUserInput | null>(null)
  const lastUserInputResolution = useRef<
    ResolvedUserInput['formValue'] | undefined
  >(undefined) // start as undefined because non-undefined hasn't been resolved yet

  const isLoadingRef = useRef(false)
  const [isLoading, setIsLoading] = useState(false)

  const bubbleIsLoadingIfChanged = () => {
    const newIsLoading =
      !!formValueBeingResolved.current || !!userInputBeingResolved.current

    if (
      newIsLoading !== isLoadingRef.current // Ref prevents firing a change when no change occured
    ) {
      isLoadingRef.current = newIsLoading
      setIsLoading(newIsLoading)
      onIsLoading?.(newIsLoading)
    }
  }

  const fireUpdatedState = useEffectEvent(
    (newState?: {
      error: Error | undefined
      display: ResolvedUserInput['display'] | undefined
    }) => {
      bubbleIsLoadingIfChanged()
      if (newState) {
        onError?.(newState.error)
        onChangeDisplay?.(newState.display)
      }
    },
  )

  useEffect(() => {
    if (formValue === lastUserInputResolution.current) {
      // Do nothing if the render has just caught up with the last user-controlled resolution.
      // We don't want to wipe what the user is typing or refetch (disable+enable) what they just typed.
      return
    }

    const toResolve = formValue ? { formValue, option } : null
    formValueBeingResolved.current = toResolve

    if (!toResolve) {
      // Optimization: empty form value means empty user input, so we can avoid an async fetch
      setUserInput('')
      fireUpdatedState({ error: undefined, display: undefined })
      return
    }

    fireUpdatedState()
    setIsResolvingFormValue(true)
    void (async () => {
      let resolved, error
      try {
        resolved = await resolveFormValue(toResolve)
      } catch (e) {
        error = e instanceof Error ? e : new Error(`${e}`)
      }

      if (toResolve === formValueBeingResolved.current) {
        formValueBeingResolved.current = null
        setUserInput(resolved?.userInput ?? '')
        fireUpdatedState({ error, display: resolved?.display })
        setIsResolvingFormValue(false)
      }
    })()
  }, [option, resolveFormValue, formValue])

  const bubbleOnChangeIfChanged = (newFormValue: typeof formValue) => {
    if (newFormValue !== formValue) {
      onChange?.(newFormValue)
    }
  }

  const handleChange = (userInput: string, newOption?: string) => {
    setUserInput(userInput)

    const toResolve = userInput
      ? { userInput, option: newOption ?? option }
      : null
    userInputBeingResolved.current = toResolve
    bubbleIsLoadingIfChanged()

    if (!toResolve) {
      // Optimization: empty user input means empty form value, so we can avoid an async fetch
      lastUserInputResolution.current = undefined
      bubbleOnChangeIfChanged(undefined)
      onError?.(undefined)
      onChangeDisplay?.(undefined)
      return
    }

    void (async () => {
      let resolved, error
      try {
        resolved = await resolveUserInput(toResolve)
      } catch (e) {
        error = e instanceof Error ? e : new Error(`${e}`)
      }

      if (toResolve === userInputBeingResolved.current) {
        userInputBeingResolved.current = null
        lastUserInputResolution.current = resolved?.formValue
        bubbleIsLoadingIfChanged()
        bubbleOnChangeIfChanged(resolved?.formValue)
        onError?.(error)
        onChangeDisplay?.(resolved?.display)
      }
    })()
  }

  const addOnProps = optionsAddOnProps
    ? {
        element: (
          <RawSelect
            aria-label={optionsAddOnProps['aria-label']}
            options={optionsAddOnProps.options}
            shouldShowClearButton={false}
            value={option}
            renderFn={optionsAddOnProps.renderFn}
            inputClassName={cx(
              optionsAddOnProps.className,
              'min-w-fit!', // allow smaller than default min width for text like inputs
            )}
            disabled={disabled}
            isInError={isInError}
            onChange={(newOption) => {
              if (newOption) {
                setOption(newOption)
                handleChange(userInput, newOption)
                setTimeout(() => {
                  // focus inputRef after value updates on next tick
                  rawInputRef.current?.focus()
                }, 0)
              }
            }}
            isAddOn
          />
        ),
        className: 'px-0!',
      }
    : undefined

  return (
    <>
      <RawTextInput
        value={userInput}
        onChange={(e) => handleChange(e.target.value)}
        disabled={isResolvingFormValue || disabled}
        ref={rawInputRef}
        isInError={isInError}
        isLoading={isLoading}
        {...restProps}
        addOnProps={addOnProps}
      />
      {/* hidden input to make vanilla form submit work */}
      {name && <input type="hidden" name={name} value={formValue ?? ''} />}
    </>
  )
}
